import { create } from 'zustand';
import produce from 'immer';
import { PublicKey } from '@solana/web3.js';

export type TokenAccountInfo = {
	pubkey: PublicKey;
	mint: PublicKey;
	programId: PublicKey;
	parsedBalance: number;
};

export type TokenAccountMap = {
	[mintAddress: string]: TokenAccountInfo | null;
};

export interface SPLTokenAccountsStore {
	// Core state
	tokenAccounts: TokenAccountMap;
	lastSyncedWallet: string | null;

	// Methods
	set: (x: (s: SPLTokenAccountsStore) => void) => void;
	get: () => SPLTokenAccountsStore;

	// Helper methods
	getTokenAccount: (mintAddress: string) => TokenAccountInfo | null;
	setLastSyncedWallet: (walletPubkey: PublicKey | null) => void;
	updateTokenAccountBalance: (mintAddress: string, balance: number) => void;
}

const DEFAULT_STATE: Pick<
	SPLTokenAccountsStore,
	'tokenAccounts' | 'lastSyncedWallet'
> = {
	tokenAccounts: {},
	lastSyncedWallet: null,
};

export const useTokenAccountsStore = create<SPLTokenAccountsStore>(
	(_set, get) => {
		const set = (fn: (s: SPLTokenAccountsStore) => void) => _set(produce(fn));

		return {
			...DEFAULT_STATE,

			// Core methods
			set,
			get,

			// Helper methods
			getTokenAccount: (mintAddress: string) => {
				return get().tokenAccounts[mintAddress] ?? null;
			},

			setLastSyncedWallet: (walletPubkey: PublicKey | null) => {
				set((state) => {
					state.lastSyncedWallet = walletPubkey?.toBase58() ?? null;
				});
			},

			updateTokenAccountBalance: (mintAddress: string, balance: number) => {
				set((state) => {
					const tokenAccount = state.tokenAccounts[mintAddress];
					if (!tokenAccount) return;
					tokenAccount.parsedBalance = balance;
				});
			},
		};
	}
);
